import { Box, Chip, Divider, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import ActivityReportForm from "../../components/freelancer/activity-reports/ActivityReportForm";
import ErrorDisplay from "../../components/freelancer/activity-reports/ErrorDisplay";
import LoadingSkeleton from "../../components/freelancer/activity-reports/LoadingSkeleton";
import { useCurrentFreelancer } from "../../hooks/useFreelancer";
import {
	useBulkChangeFreelancerActivityReportStatus,
	useSearchFreelancerActivityReports,
	useUpdateFreelancerActivityReport
} from "../../hooks/useFreelancerActivityReports";
import { zoullooColors } from "../../theme";
import { useEnumTranslation } from "../../utils/translateEnum";
import { ArrowForward } from "@mui/icons-material";
import { useFreelancerProposalByProject } from "../../hooks/useFreelancerProposal";
import { FreelancerProposalPricingType } from "../../models/FreelancerProposal";

interface FreelancerActivityReportsProps {
	projectId?: number;
}

const FreelancerActivityReports: React.FC<FreelancerActivityReportsProps> = ({
	projectId
}) => {
	const { t } = useTranslation();
	const location = useLocation();
	let { id: freelancerActivityReportKey } = useParams<{ id: string }>();

	freelancerActivityReportKey = location.pathname.includes("/activity-reports/")
		? freelancerActivityReportKey
		: "";

	const { data: freelancer } = useCurrentFreelancer();
	// Fetch activity reports using the API hook
	const {
		data: reports = [],
		isLoading,
		error,
		refetch
	} = useSearchFreelancerActivityReports(
		{
			projectId,
			freelancerProfileId: freelancer?.profile.id
		},
		freelancerActivityReportKey,
		(!!projectId || freelancerActivityReportKey !== "") && !!freelancer?.profile.id
	);
	const { translateProjectStatus } = useEnumTranslation();
	const updateMutation = useUpdateFreelancerActivityReport();
	const bulkChangeStatusMutation = useBulkChangeFreelancerActivityReportStatus();
	const { data: proposal } = useFreelancerProposalByProject(reports?.[0]?.project.id);

	if (isLoading) {
		return <LoadingSkeleton />;
	}

	if (error) {
		return (
			<Box px={6} py={2}>
				<ErrorDisplay onRetry={() => refetch()} />
			</Box>
		);
	}

	const project = reports[0].project;
	const user = reports[0].freelancerProfile.freelancer!.user;

	return (
		<Box px={6} py={2}>
			{/* Header - Project Info */}
			<Paper
				elevation={0}
				sx={{
					mb: 4,
					borderRadius: 3,
					overflow: "hidden",
					background: "white"
				}}
			>
				{/* Top Bar with Project Status */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						px: 3,
						py: 1.5,
						bgcolor: zoullooColors.secondary.alpha,
						borderBottom: "2px solid",
						borderColor: zoullooColors.secondary.light
					}}
				>
					{/* Project Title Section */}
					<Box display="flex" alignItems="center" gap={1.5} mb={1}>
						<Box flex={1}>
							<Typography
								variant="h3"
								fontWeight="700"
								sx={{ color: "text.primary", lineHeight: 1.2 }}
							>
								{project.name}
							</Typography>

							<Box display="flex" alignItems="center" gap={3}>
								<Typography variant="body1" sx={{ color: "text.secondary" }}>
									{t("activityReports.projectNumber")} {project.projectNumber}
								</Typography>
								{location.pathname.includes("/activity-reports/") && (
									<Link
										color="secondary"
										to={`/projects/${project.id}`}
										style={{
											color: zoullooColors.text.primary,
											textTransform: "none",
											paddingLeft: 1,
											paddingRight: 1,
											minWidth: "auto",
											fontSize: "0.875rem",
											fontWeight: 500,
											display: "flex",
											alignItems: "center",
											gap: 4,
											marginBottom: 2
										}}
									>
										{t("activityReports.accessProject")}{" "}
										<ArrowForward sx={{ fontSize: "1rem" }} />
									</Link>
								)}
							</Box>
						</Box>
					</Box>
					<Chip
						label={translateProjectStatus(project.status)}
						size="medium"
						sx={{
							bgcolor: "info.light",
							color: "white",
							fontSize: "1rem",
							py: 3,
							height: 28
						}}
					/>
				</Box>

				{/* Divider */}
				<Divider sx={{ mx: 3, mb: 2 }} />

				{/* Mission Summary Section */}
				<Box sx={{ px: 4, py: 3 }}>
					<Grid container spacing={3}>
						{/* Left Column - Info */}
						<Grid size={{ xs: 12, md: 5 }}>
							<Box
								sx={{
									height: "100%",
									display: "flex",
									flexDirection: "column",
									gap: 2.5
								}}
							>
								{/* Talent Info */}
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 2,
										p: 2,
										borderRadius: 2,
										bgcolor: "grey.50",
										border: "1px solid",
										borderColor: "grey.200"
									}}
								>
									<Box>
										<Typography
											variant="h5"
											color="text.secondary"
											textTransform="uppercase"
										>
											{t("activityReports.talent")}
										</Typography>
										<Typography variant="body1" sx={{ mt: 0.5 }}>
											{user.fullName}
										</Typography>
									</Box>
								</Box>

								{/* Period Info */}
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 2,
										p: 2,
										borderRadius: 2,
										bgcolor: "grey.50",
										border: "1px solid",
										borderColor: "grey.200"
									}}
								>
									<Box>
										<Typography
											variant="h5"
											color="text.secondary"
											textTransform="uppercase"
										>
											{t("activityReports.missionPeriod")}
										</Typography>
										<Typography variant="body1" sx={{ mt: 0.5 }}>
											{t("activityReports.from")} &nbsp;&nbsp;
											{format(
												new Date(project.startDate || project.desiredStartDate!),
												"dd/MM/yyyy"
											)}
											&nbsp;&nbsp; {t("activityReports.to")} &nbsp;&nbsp;
											{format(new Date(project.endDate!), "dd/MM/yyyy")}
										</Typography>
									</Box>
								</Box>
							</Box>
						</Grid>

						{/* Right Column - Stats */}
						<Grid size={{ xs: 12, md: 7 }}>
							<Box
								sx={{
									height: "100%",
									p: 3,
									borderRadius: 2,
									bgcolor: "grey.50",
									border: "1px solid",
									borderColor: "grey.200"
								}}
							>
								<Typography
									variant="h5"
									color="text.secondary"
									textTransform="uppercase"
									sx={{ mb: 4 }}
								>
									{proposal?.pricingMode === FreelancerProposalPricingType.Daily
										? t("activityReports.daysTracking")
										: t("activityReports.hoursTracking")}
								</Typography>
								<Grid container spacing={2}>
									<Grid size={{ xs: 4 }}>
										<Box textAlign="center">
											<Typography
												variant="body2"
												color="text.secondary"
												fontWeight="600"
												display="block"
												mb={0.5}
												textTransform={"uppercase"}
											>
												{t("activityReports.contractual")}
											</Typography>
											<Typography variant="h2" sx={{ color: "text.primary" }}>
												{proposal?.tasks?.reduce(
													(sum, task) => sum + (task.quantity || 0),
													0
												) || 0}
											</Typography>
										</Box>
									</Grid>
									<Grid size={{ xs: 4 }}>
										<Box textAlign="center">
											<Typography
												variant="body2"
												color="text.secondary"
												fontWeight="600"
												display="block"
												mb={0.5}
												textTransform={"uppercase"}
											>
												{t("activityReports.completed")}
											</Typography>
											<Typography variant="h2" sx={{ color: "text.primary" }}>
												{reports.reduce((sum, report) => {
													const units = report.workedUnits || 0;
													return sum + units;
												}, 0)}
											</Typography>
										</Box>
									</Grid>
									<Grid size={{ xs: 4 }}>
										<Box textAlign="center">
											<Typography
												variant="body2"
												color="text.secondary"
												fontWeight="600"
												display="block"
												mb={0.5}
												textTransform={"uppercase"}
											>
												{t("activityReports.available")}
											</Typography>
											<Typography variant="h2" sx={{ color: "text.primary" }}>
												{(proposal?.tasks?.reduce(
													(sum, task) => sum + (task.quantity || 0),
													0
												) || 0) -
													reports.reduce((sum, report) => {
														const units = report.workedUnits || 0;
														return sum + units;
													}, 0)}
											</Typography>
										</Box>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Paper>

			{/* Activity Reports Accordions */}
			{reports.map(report => {
				const contractualDays = proposal?.tasks?.reduce(
					(sum, task) => sum + (task.quantity || 0), 0
				) || 0;
				const consumedDaysByOtherReports = reports
					.filter(r => r.id !== report.id)
					.reduce((sum, r) => sum + (r.workedUnits || 0), 0);

				return (
					<ActivityReportForm
						key={report.id}
						report={report}
						contractualDays={contractualDays}
						consumedDaysByOtherReports={consumedDaysByOtherReports}
						updateMutation={updateMutation}
						bulkChangeStatusMutation={bulkChangeStatusMutation}
					/>
				);
			})}
		</Box>
	);
};

export default FreelancerActivityReports;
